import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import {
  Reward,
  RewardsManager,
  RewardType,
} from '@/game/GameComponents/PhaserGame/Utils/RewardsManager';

/* START OF COMPILED CODE */

import DefaultButton from '../GeneralUI/Buttons/DefaultButton';
/* START-USER-IMPORTS */
import EventsManager from '@/game/GameComponents/PhaserGame/Managers/EventsManager';
import unlockedMasteryFeedackPanel from '@/game/scenes/unlockedMasteryFeedackPanel';
/* END-USER-IMPORTS */

export default class RewardFeedbackPanel extends Phaser.GameObjects.Container {
  constructor(scene: Phaser.Scene, x?: number, y?: number) {
    super(scene, x ?? 0, y ?? 0);

    // blocker
    const blocker = scene.add.image(512, 384, 'placeholder-square');
    blocker.scaleY = 2;
    blocker.alpha = 0.3;
    blocker.alphaTopLeft = 0.31;
    blocker.alphaTopRight = 0.3;
    blocker.alphaBottomLeft = 0.3;
    blocker.alphaBottomRight = 0.3;
    blocker.tintTopLeft = 0;
    blocker.tintTopRight = 0;
    blocker.tintBottomLeft = 0;
    blocker.tintBottomRight = 0;
    this.add(blocker);

    // backing
    const backing = scene.add.nineslice(
      512,
      384,
      'Hud2',
      undefined,
      160,
      128,
      4,
      4,
      4,
      8,
    );
    backing.scaleX = 2;
    backing.scaleY = 2;
    this.add(backing);

    // rewardIcon
    const rewardIcon = scene.add.image(512, 384, 'hud-icons', 95);
    rewardIcon.scaleX = 2;
    rewardIcon.scaleY = 2;
    rewardIcon.setOrigin(1, 0.5);
    this.add(rewardIcon);

    // claimButton
    const claimButton = new DefaultButton(scene, 512, 456);
    this.add(claimButton);

    // taskLabel
    const taskLabel = scene.add.bitmapText(
      512,
      344,
      'Aronui',
      'Daily Complete',
    );
    taskLabel.setOrigin(0.5, 0.5);
    taskLabel.tintTopLeft = 5387071;
    taskLabel.tintTopRight = 5387071;
    taskLabel.tintBottomLeft = 5387071;
    taskLabel.tintBottomRight = 5387071;
    taskLabel.text = 'Daily Complete';
    taskLabel.fontSize = -14;
    this.add(taskLabel);

    // titleText
    const titleText = scene.add.bitmapText(
      512,
      312,
      'Aronui',
      'Reward Unlocked!',
    );
    titleText.setOrigin(0.5, 0.5);
    titleText.tintTopLeft = 985612;
    titleText.tintTopRight = 985612;
    titleText.tintBottomLeft = 985612;
    titleText.tintBottomRight = 985612;
    titleText.text = 'Reward Unlocked!';
    titleText.fontSize = -30;
    this.add(titleText);

    // rewardLabel
    const rewardLabel = scene.add.bitmapText(512, 386, 'Pixellari', '50');
    rewardLabel.setOrigin(0, 0.5);
    rewardLabel.tintTopLeft = 15091001;
    rewardLabel.tintTopRight = 15091001;
    rewardLabel.tintBottomLeft = 15091001;
    rewardLabel.tintBottomRight = 15091001;
    rewardLabel.text = '50';
    rewardLabel.fontSize = -24;
    this.add(rewardLabel);

    // claimButton (prefab fields)
    claimButton.buttonText = 'Claim';

    this.blocker = blocker;
    this.rewardIcon = rewardIcon;
    this.claimButton = claimButton;
    this.taskLabel = taskLabel;
    this.rewardLabel = rewardLabel;

    /* START-USER-CTR-CODE */
    this.blocker.setInteractive({ cursor: 'pointer' });

    this.claimButton.controller.setSubmissionCallback(() => {
      this.claimReward();
    });
    EventsManager.registerEventOn(
      this.scene,
      GAME_EVENTS.REWARD_EARNED,
      this.showNextReward,
      this,
    );
    this.scene.events.on('awake', this.showNextReward);

    /* END-USER-CTR-CODE */
  }

  private blocker: Phaser.GameObjects.Image;
  private rewardIcon: Phaser.GameObjects.Image;
  private claimButton: DefaultButton;
  private taskLabel: Phaser.GameObjects.BitmapText;
  private rewardLabel: Phaser.GameObjects.BitmapText;

  /* START-USER-CODE */
  currentReward?: Reward;
  isShowingReward: boolean = false;
  private hasShownDailyRewardThisTime = false;

  showNextReward() {
    if (this.isShowingReward) return;
    //this.isShowingReward = true;
    let canShow = !unlockedMasteryFeedackPanel.isShowingMasteryFeedback;

    if (canShow) {
      this.nextReward();
    } else {
      const waitUntilCanShowReward = new Promise<string>((resolve) => {
        const check = setInterval(() => {
          canShow = !unlockedMasteryFeedackPanel.isShowingMasteryFeedback;
          if (canShow) {
            resolve('');
            clearInterval(check);
          }
        }, 500);
      });
      waitUntilCanShowReward.then(() => this.nextReward());
    }
  }

  nextReward() {
    this.setVisible(true);
    if (RewardsManager.HasRewardAvailable()) {
      this.showReward(RewardsManager.GetNextReward());
    } else {
      // Close the reward panel.
      if (this.hasShownDailyRewardThisTime) {
        EventBus.emit(GAME_EVENTS.FINISHED_DISPLAYING_DAILY_REWARD);
      }
      this.hasShownDailyRewardThisTime = false;
      this.setVisible(false);
    }
  }

  showReward(reward: Reward | undefined) {
    if (!reward) return;
    this.isShowingReward = true;
    this.currentReward = reward;
    this.rewardIcon.setTexture('hud-icons', 95); // Ruby.
    this.taskLabel.setText(reward.rewardCompleteLabel);
    this.rewardLabel.setText(reward.rubies.toString());
    if (
      reward.rewardType === RewardType.DAILY_DUEL ||
      reward.rewardType === RewardType.DAILY_MINIGAME
    ) {
      this.hasShownDailyRewardThisTime = true;
    }
    if (reward.autoAccept) {
      this.claimReward();
      return;
    }
  }

  claimReward() {
    this.isShowingReward = false;
    EventBus.emit(GAME_EVENTS.PLAYER_CLAIMED_REWARD, this.currentReward);
    this.showNextReward();
  }

  preDestroy() {
    this.scene.events.off('awake', this.showNextReward);
  }

  /* END-USER-CODE */
}

/* END OF COMPILED CODE */

// You can write more code here
